import * as React from 'react';
import ResizeObserver from 'resize-observer-polyfill';
import {relative, equal, type Box} from '@packages/utilities/box';
import {getSurfaceBox, useOverlay} from './OverlayContext';

type Props = {
  children: (
    ref: React.RefObject<any>,
    arg1: Box | undefined
  ) => React.ReactElement;
  active?: boolean;
  targetRef?: React.RefObject<any>;
};

// Refs may resolve to a class component instance (when a child is a class
// component) or to null (when a child is a function component without
// forwardRef). Guard against anything that isn't a DOM Element so the
// ResizeObserver below never throws and crashes the React tree.
//
// When the guard short-circuits for a non-null, non-Element ref, the
// tooltip/popover using this Locator will silently position at (0,0)
// instead of next to its anchor — the anchor `box` stays `undefined`.
//
// Dedupe console + telemetry: fire at most once per unexpected ref object /
// per Locator ref instance. WeakSets are always allocated so production RUM
// dedupes the same way dev console does.
const warnedNonElementResults = new WeakSet<object>();
const warnedNullRefKeys = new WeakSet<object>();

export type LocatorBugPayload =
  | {
      warningType: 'locatorNonElementRef';
      pathname?: string;
      refConstructorName?: string;
    }
  | {
      warningType: 'locatorAnchorRefNull';
      pathname?: string;
    };

// Consumer (e.g. designer `initialize.ts`) wires this to
// `telemetry.sendEvent('datadog::…')`. Default no-op keeps overlay free of
// a designer-telemetry dependency. Pairs with the dev console.warn below
// so QA gets pre-merge visibility (console) and production gets
// observability (RUM) for the same broken-overlay class of bug.
let _reportLocatorBug = (_payload: LocatorBugPayload) => {};

export const setLocatorBugReporter = (
  fn: (payload: LocatorBugPayload) => void
) => {
  _reportLocatorBug = fn;
};

const getPathname = () =>
  typeof window !== 'undefined' && window.location
    ? window.location.pathname
    : undefined;

const warnNonElementRef = (result: unknown) => {
  if (typeof result === 'object' && result !== null) {
    if (warnedNonElementResults.has(result)) return;
    warnedNonElementResults.add(result);
  }

  const refConstructorName =
    typeof result === 'object' &&
    result !== null &&
    'constructor' in result &&
    typeof (result as {constructor?: {name?: string}}).constructor?.name ===
      'string'
      ? (result as {constructor: {name: string}}).constructor.name
      : undefined;

  try {
    _reportLocatorBug({
      warningType: 'locatorNonElementRef',
      pathname: getPathname(),
      refConstructorName,
    });
  } catch {
    // Never let telemetry break rendering.
  }

  if (process.env.NODE_ENV === 'production') return;
  console.warn(
    '[Locator] Ref resolved to a non-Element value; the tooltip, popover, ' +
      'or overlay using this Locator will not position correctly relative ' +
      'to its anchor. The child passed to the parent overlay component ' +
      '(tooltipTrigger, Positioner, etc.) should be a `React.forwardRef` ' +
      'component or a host element so the ref can reach a DOM node.',
    result
  );
};

// Companion warning for the OTHER failure mode getNode can't catch:
// the ref was attempted but stayed null. This happens when the child is a
// plain function component without `React.forwardRef` — React silently
// drops the ref instead of populating it. The Locator then has nothing to
// measure. Dedup'd globally per Locator instance so we don't flood on
// re-renders.
const warnNullRef = (refKey: object) => {
  if (warnedNullRefKeys.has(refKey)) return;
  warnedNullRefKeys.add(refKey);

  try {
    _reportLocatorBug({
      warningType: 'locatorAnchorRefNull',
      pathname: getPathname(),
    });
  } catch {
    // Never let telemetry break rendering.
  }

  if (process.env.NODE_ENV === 'production') return;
  console.warn(
    '[Locator] Anchor ref is null after mount — the child component is ' +
      'not forwardRef-compatible. Tooltip / popover / overlay will not ' +
      'position correctly relative to its anchor. The child passed to ' +
      'the parent overlay component (tooltipTrigger, Positioner, etc.) ' +
      'should be a `React.forwardRef` component or a host element so ' +
      'the ref can reach a DOM node. (See `tooltipTrigger.js` cloneElement ' +
      'site for the injection point.)'
  );
};

const auditNode = (result: unknown): Element | null => {
  if (!result) {
    return null;
  }
  // The `instanceof Element` check excludes text nodes here
  // (Text inherits from Node, not Element), so the previous explicit
  // `nodeType === Node.TEXT_NODE` branch was dead code after this guard
  // widened the input type from `Element` to `unknown`.
  if (typeof Element !== 'undefined' && !(result instanceof Element)) {
    warnNonElementRef(result);
    return null;
  }
  return result as Element;
};

// Tracks the DOM element that an overlay should appear next to and measures
// its position relative to the current overlay surface. It gives the child a
// ref to attach to that element and its measured box so the overlay can be
// placed in the correct position.
function LocatorImpl({children, active = true, targetRef: externalRef}: Props) {
  const [box, setBoxState] = React.useState<Box | undefined>();

  const internalRef = React.useRef<any>(null);
  const [targetRef] = React.useState<React.RefObject<any>>(
    () => externalRef ?? internalRef
  );

  // Used to store whether we have an update scheduled to prevent
  // multiple `box` calculations happening at the same time.
  const scheduledRef = React.useRef(false);
  const resizeObserverRef = React.useRef<ResizeObserver | null>(null);
  const currentNodeRef = React.useRef<Element | null>(null);

  const overlayContext = useOverlay();

  // We are using refs but assigning them to their latest value on every render
  // to keep these values current while preserving a stable `setBox` callback.
  // Without these latest-value refs, `setBox` could read values captured by an
  // earlier render. Recreating `setBox` whenever a value changes would also be
  // problematic because the ResizeObserver retains the callback it was created
  // with.
  const contextRef = React.useRef(overlayContext);
  const activeRef = React.useRef(active);
  const boxRef = React.useRef(box);
  contextRef.current = overlayContext;
  activeRef.current = active;
  boxRef.current = box;

  const setBox = React.useCallback(() => {
    if (!targetRef.current) {
      return;
    }

    // ensure we only schedule at most one update at a time to prevent
    // infinite rendering loops
    if (scheduledRef.current) {
      return;
    }

    // if we transitioned from active to inactive we may still have a setBox
    // scheduled. This ensures it bails out as a noop.
    if (activeRef.current === false) {
      return;
    }

    const selfNode = auditNode(targetRef.current);

    if (selfNode !== currentNodeRef.current) {
      currentNodeRef.current = selfNode;
      let observer = resizeObserverRef.current;
      if (selfNode) {
        if (observer == null) {
          observer = new ResizeObserver(setBox);
          resizeObserverRef.current = observer;
        } else {
          // reconnect the observer to the current node
          observer.disconnect();
        }
        observer.observe(selfNode);
      } else {
        if (observer) {
          observer.disconnect();
          resizeObserverRef.current = null;
        }
      }
    }

    if (selfNode && typeof selfNode.getBoundingClientRect === 'function') {
      const newBox = relative(getSurfaceBox(contextRef.current))(
        selfNode.getBoundingClientRect()
      );
      const oldBox = boxRef.current;
      // be a bit more pessimistic on scheduling work
      if (!oldBox || !equal(oldBox, newBox)) {
        scheduledRef.current = true;
        setBoxState(newBox);
      }
    }
  }, [targetRef]);

  const cleanup = React.useCallback(() => {
    if (resizeObserverRef.current) {
      resizeObserverRef.current.disconnect();
      resizeObserverRef.current = null;
    }
    currentNodeRef.current = null;
  }, []);

  // When active and mounted, the ref should have been populated by the
  // rendered child. If it's still null, the child failed to forward the ref
  // (e.g. plain function component without forwardRef). Warn once per
  // Locator instance in dev; in production still emit RUM via
  // `setLocatorBugReporter` so mispositioned overlays are observable.
  const checkRefAttached = React.useCallback(() => {
    if (!activeRef.current) return;
    if (targetRef.current == null) {
      warnNullRef(targetRef);
    }
  }, [targetRef]);

  // This is the closest way we can migrate the old componentDidMount + componentDidUpdate.
  // No dependency array, so this runs after every commit like the class lifecycle did, where
  // it remeasured on any update, including overlay-context changes forced by <RefreshOverlay>.
  const mountedRef = React.useRef(false);

  // To simulate the old class component `prevProps` inside componentDidUpdate.
  const prevActiveRef = React.useRef(active);

  React.useLayoutEffect(() => {
    const wasScheduled = scheduledRef.current;

    if (!mountedRef.current) {
      mountedRef.current = true;
      setBox();
      checkRefAttached();
      prevActiveRef.current = active;
      return;
    }

    const prevActive = prevActiveRef.current;
    if (active) {
      setBox();
      // Re-check on transitions from inactive → active (e.g. tooltip
      // opening for the first time after the page mounted).
      if (!prevActive) checkRefAttached();
    } else if (prevActive) {
      cleanup();
    }
    prevActiveRef.current = active;

    // Keep the guard set until the state-driven commit has skipped remeasurement.
    if (wasScheduled) {
      scheduledRef.current = false;
    }
  });

  // Rough equivalent of componentWillUnmount.
  React.useLayoutEffect(
    () => () => {
      cleanup();
      mountedRef.current = false;
    },
    [cleanup]
  );

  return children(targetRef, box);
}

export const Locator = React.memo(LocatorImpl);
Locator.displayName = 'Locator';
